/**
 * Célula operacional do cronômetro da Ordem de Serviço.
 * Mantém o mesmo padrão visual de Tarefas e usa exclusivamente as RPCs oficiais.
 *
 * RELEASE: 2026-10-09 v1.3.0 - Replica as cores semânticas por situação da OS.
 * RELEASE: 2026-10-09 v1.2.1 - Reforça o contraste do cronômetro parado e pausado.
 * RELEASE: 2026-10-09 v1.2.0 - Contraste por estado alinhado ao cronômetro de Retenção.
 * RELEASE: 2026-10-09 v1.1.0 - Paridade visual e temporal com o cronômetro de Retenção.
 * RELEASE: 2026-10-09 v1.0.0 - Iniciar, pausar e retomar diretamente na listagem.
 */
import { useCallback, useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { formatServiceOrderDuration } from '@/lib/serviceOrderTiming';
import { SERVICE_ORDER_STARTABLE, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import { serviceOrderTimerTone } from './ServiceOrderTiming';

const logger = createLogger('ServiceOrderTimerCell');
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as any).call(supabase, name, args);

interface Props {
  serviceOrderId: string;
  status: ServiceOrderStatus;
  seconds: number;
  isPaused: boolean;
  canControl: boolean;
}

export function ServiceOrderTimerCell({ serviceOrderId, status, seconds, isPaused, canControl }: Props) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const [clock, setClock] = useState({ serverSeconds: seconds, receivedAt: Date.now(), now: Date.now() });
  const [loading, setLoading] = useState(false);

  // Reinicia a referência quando chega um valor novo do servidor, evitando somar
  // novamente os segundos já persistidos após uma atualização da consulta.
  useEffect(() => {
    const now = Date.now();
    setClock({ serverSeconds: seconds, receivedAt: now, now });
  }, [seconds, status, isPaused]);

  useEffect(() => {
    if (status !== 'in_progress' || isPaused) return undefined;
    const interval = window.setInterval(() => setClock((value) => ({ ...value, now: Date.now() })), 1000);
    return () => window.clearInterval(interval);
  }, [status, isPaused]);

  /** Atualiza todas as superfícies oficiais que apresentam a OS. */
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['service-orders'] });
    queryClient.invalidateQueries({ queryKey: ['service-order-details', serviceOrderId] });
    queryClient.invalidateQueries({ queryKey: ['service-order-timeline', serviceOrderId] });
  }, [queryClient, serviceOrderId]);

  const run = async (action: 'start' | 'pause' | 'resume') => {
    if (loading) return;
    setLoading(true);
    try {
      const args = action === 'start'
        ? { p_id: serviceOrderId, p_status: 'in_progress', p_note: null, p_stock: null, p_tenant_hint: currentTenant?.id ?? null }
        : { p_id: serviceOrderId, ...(action === 'pause' ? { p_reason: null } : {}), p_tenant_hint: currentTenant?.id ?? null };
      const functionName = action === 'start'
        ? 'change_service_order_status'
        : action === 'pause' ? 'pause_service_order' : 'resume_service_order';
      const { error } = await rpc(functionName, args);
      if (error) throw error;
      toast.success(action === 'start' ? 'Ordem de Serviço iniciada' : action === 'pause' ? 'Cronômetro pausado' : 'Cronômetro retomado');
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível alterar o cronômetro';
      logger.error('Erro ao controlar o cronômetro da OS', { action, message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const canStart = SERVICE_ORDER_STARTABLE.includes(status as ServiceOrderStatus);
  const canPause = status === 'in_progress' && !isPaused;
  const canResume = status === 'in_progress' && isPaused;
  const visibleSeconds = clock.serverSeconds + (canPause ? Math.floor((clock.now - clock.receivedAt) / 1000) : 0);
  // A cor comunica a situação mesmo quando o usuário não possui permissão de controle.
  const timerClass = serviceOrderTimerTone(status, canResume);

  return (
    <div className="flex items-center justify-center gap-1.5" onClick={(event) => event.stopPropagation()}>
      <span className={`font-mono text-[11px] tabular-nums ${timerClass}`}>
        {formatServiceOrderDuration(visibleSeconds)}
      </span>
      {canControl && canStart && (
        <Button variant="ghost" size="icon" className="h-6 w-6 text-success hover:bg-success/15 hover:text-success" disabled={loading} title="Iniciar Ordem de Serviço" onClick={() => run('start')}>
          <Play className="h-3.5 w-3.5" fill="currentColor" />
        </Button>
      )}
      {canControl && canPause && (
        <Button variant="ghost" size="icon" className="h-6 w-6 text-warning hover:bg-warning/15 hover:text-warning" disabled={loading} title="Pausar cronômetro" onClick={() => run('pause')}>
          <Pause className="h-3.5 w-3.5" fill="currentColor" />
        </Button>
      )}
      {canControl && canResume && (
        <Button variant="ghost" size="icon" className="h-6 w-6 text-success hover:bg-success/15 hover:text-success" disabled={loading} title="Retomar cronômetro" onClick={() => run('resume')}>
          <Play className="h-3.5 w-3.5" fill="currentColor" />
        </Button>
      )}
    </div>
  );
}